/**
 * sumarioModels — Catálogo de modelos de sumário ("Escolher sumário").
 *
 * Fonte única de verdade dos 4 modelos convencionais que semeiam um esqueleto
 * de destinos EM ORDEM (Introdução → miolo → Conclusão). Espelha o padrão de
 * fonte-única de semantic-tags.ts.
 *
 * O 5º "modelo" — `livre` — é o legado (digitação livre por "+ Criar destino"):
 * NÃO tem entrada no catálogo; representado por `activeModel` ausente no
 * DestinoData. Por isso o catálogo é chaveado por SumarioModelCatalogKey
 * (exclui `livre`), enquanto `activeModel`/`parkedModels` usam SumarioModelKey.
 *
 * Sprint 034 — Fundação Destino (Modelos de Sumário).
 */

/** Chave de modelo persistível (inclui o pseudo-modelo `livre` do legado). */
export type SumarioModelKey = 'tese' | 'artigo' | 'conferencia' | 'projeto' | 'livre';

/** Modelos com entrada de catálogo (os 4 convencionais; exclui `livre`). */
export type SumarioModelCatalogKey = Exclude<SumarioModelKey, 'livre'>;

interface RepeatableConfig {
  /** Rótulo singular do item repetível (ex.: "Capítulo") — usado no botão "+ Incluir". */
  singular: string;
  /** Gera o nome do n-ésimo item (1-based). */
  gerarNome: (n: number) => string;
}

export interface SumarioModel {
  key: SumarioModelCatalogKey;
  label: string;
  kind: 'repeatable' | 'fixed';
  /** Partes fixas no início, em ordem (ex.: ["Introdução"]). */
  bookendsInicio: string[];
  /** Config do miolo repetível — presente sse kind === 'repeatable'. */
  repeatable?: RepeatableConfig;
  /** Partes fixas no fim, em ordem (ex.: ["Conclusão"]). */
  bookendsFim: string[];
  /** Lista fixa completa, em ordem — presente sse kind === 'fixed'. */
  partesFixas?: string[];
}

/** Converte 1..N em numeral romano (I, II, III, IV, …). n<=0 → vazio (guarda). */
export function toRomano(n: number): string {
  if (n <= 0) return '';
  const tabela: ReadonlyArray<readonly [number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let resto = Math.floor(n);
  let out = '';
  for (const [valor, simbolo] of tabela) {
    while (resto >= valor) {
      out += simbolo;
      resto -= valor;
    }
  }
  return out;
}

/** Catálogo único dos 4 modelos. Chaveado por SumarioModelCatalogKey. */
export const SUMARIO_MODELS: Record<SumarioModelCatalogKey, SumarioModel> = {
  tese: {
    key: 'tese',
    label: 'Tese / Dissertação / Livro',
    kind: 'repeatable',
    bookendsInicio: ['Introdução'],
    repeatable: { singular: 'Capítulo', gerarNome: (n) => `Capítulo ${n}` },
    bookendsFim: ['Conclusão'],
  },
  artigo: {
    key: 'artigo',
    label: 'Artigo',
    kind: 'repeatable',
    bookendsInicio: ['Introdução'],
    repeatable: { singular: 'Seção', gerarNome: (n) => `Seção ${n}` },
    bookendsFim: ['Conclusão'],
  },
  conferencia: {
    key: 'conferencia',
    label: 'Conferência / Comunicação',
    kind: 'repeatable',
    bookendsInicio: ['Abertura'],
    repeatable: { singular: 'Parte', gerarNome: (n) => toRomano(n) },
    bookendsFim: ['Conclusão'],
  },
  projeto: {
    key: 'projeto',
    label: 'Projeto de pesquisa',
    kind: 'fixed',
    bookendsInicio: [],
    bookendsFim: [],
    partesFixas: [
      'Introdução', 'Problema', 'Justificativa', 'Objetivos',
      'Referencial teórico', 'Metodologia', 'Cronograma', 'Referências',
    ],
  },
};

/** Lista ordenada dos modelos de catálogo (para o picker da UI). */
export const SUMARIO_MODEL_LIST: ReadonlyArray<SumarioModel> = [
  SUMARIO_MODELS.tese,
  SUMARIO_MODELS.artigo,
  SUMARIO_MODELS.conferencia,
  SUMARIO_MODELS.projeto,
];

/** True se a chave tem entrada de catálogo (i.e., não é `livre`). Type guard. */
export function isCatalogModel(key: SumarioModelKey): key is SumarioModelCatalogKey {
  return key !== 'livre';
}

/** True se o modelo pede conta N ao semear (miolo repetível). */
export function isRepeatable(key: SumarioModelKey): boolean {
  return isCatalogModel(key) && SUMARIO_MODELS[key].kind === 'repeatable';
}

/**
 * Devolve a lista ordenada de nomes de parte a semear para um modelo.
 * - `livre` → [] (sem catálogo).
 * - fixo (projeto) → as partesFixas, ignorando n.
 * - repetível → [bookendsInicio, miolo(1..n), bookendsFim], com n = count (default 0).
 */
export function gerarPartes(key: SumarioModelKey, n?: number): string[] {
  if (!isCatalogModel(key)) return [];
  const model = SUMARIO_MODELS[key];
  if (model.kind === 'fixed') {
    return [...(model.partesFixas ?? [])];
  }
  const count = Math.max(0, Math.floor(n ?? 0));
  const miolo = Array.from({ length: count }, (_, i) => model.repeatable!.gerarNome(i + 1));
  return [...model.bookendsInicio, ...miolo, ...model.bookendsFim];
}
